import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type SlaEstado } from "@/lib/api";
import { useToast } from "@/ui/Toast";
import SeccionLayout, { BloqueSeccion, PieGuardar } from "../SeccionLayout";
import { AYUDA_SECCION } from "@/components/ayuda/vistas/configuracion";
import { useAppSettings, useBorradorSeccion } from "../useAppSettings";

type FilaSla = { estado: string; pq: string; q: string; ad: string };

const CAMPOS_SLA = ["pq", "q", "ad"] as const;

function filasDesde(sla: SlaEstado): FilaSla[] {
  return sla.overrides.map((o) => ({
    estado: o.estado,
    pq: o.por_quemarse?.toString() ?? "",
    q: o.quemado?.toString() ?? "",
    ad: o.admin?.toString() ?? "",
  }));
}

/**
 * Cuántos días sin movimiento tolera un caso antes de cambiar de color y
 * disparar avisos, con override opcional por estado.
 *
 * Es la única sección que guarda contra DOS endpoints (`/admin/settings` para
 * los umbrales globales y `/admin/sla-estado` para los overrides). Van juntos
 * porque el global es el placeholder del override: separarlos obligaría a ir y
 * venir para saber contra qué se compara un campo vacío.
 */
export default function TiemposEscalacion() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const { settings, cargando, guardando, guardar } = useAppSettings();
  const { valores, set, sucio } = useBorradorSeccion(settings, (s) => ({
    escalacion_dias_por_quemarse: s.escalacion_dias_por_quemarse,
    escalacion_dias_quemado: s.escalacion_dias_quemado,
    escalacion_dias_admin: s.escalacion_dias_admin,
  }));

  const [sla, setSla] = useState<SlaEstado | null>(null);
  const [filas, setFilas] = useState<FilaSla[]>([]);
  const [guardandoSla, setGuardandoSla] = useState(false);

  const cargarSla = useCallback(async () => {
    try {
      const r = await api.get<SlaEstado>("/api/admin/sla-estado");
      setSla(r);
      setFilas(filasDesde(r));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toastRef.current.error("No pude cargar el SLA por estado", msg);
    }
  }, []);

  useEffect(() => {
    void cargarSla();
  }, [cargarSla]);

  const filasSucias = !!sla && JSON.stringify(filas) !== JSON.stringify(filasDesde(sla));
  const hayCambios = sucio || filasSucias;

  const ordenGlobalOk =
    !valores ||
    (valores.escalacion_dias_por_quemarse < valores.escalacion_dias_quemado &&
      valores.escalacion_dias_quemado <= valores.escalacion_dias_admin);

  const ordenFilasOk = filas.every((f) => {
    if (f.pq === "" || f.q === "" || f.ad === "") return true;
    return Number(f.pq) < Number(f.q) && Number(f.q) <= Number(f.ad);
  });

  const error = !ordenGlobalOk
    ? "El orden debe ser: amarillo < rojo ≤ escalación a admin."
    : !ordenFilasOk
      ? "En cada estado con valores, debe cumplirse amarillo < rojo ≤ admin."
      : null;

  async function guardarTodo() {
    if (error) return;

    // El SLA va primero y sin aviso propio: si los dos cambian, un solo aviso
    // al final dice la verdad ("se guardó la sección"), no dos a medias.
    if (filasSucias) {
      setGuardandoSla(true);
      try {
        const r = await api.patch<SlaEstado>("/api/admin/sla-estado", {
          overrides: filas.map((f) => ({
            estado: f.estado,
            por_quemarse: f.pq === "" ? null : Number(f.pq),
            quemado: f.q === "" ? null : Number(f.q),
            admin: f.ad === "" ? null : Number(f.ad),
          })),
        });
        setSla(r);
        setFilas(filasDesde(r));
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude guardar el SLA por estado", msg);
        return;
      } finally {
        setGuardandoSla(false);
      }
    }

    if (sucio && valores) {
      await guardar(valores, "Tiempos y escalación guardados");
    } else if (filasSucias) {
      toast.success("Tiempos y escalación guardados");
    }
  }

  if (cargando || !valores) {
    return <p className="text-sm text-zinc-500">Cargando…</p>;
  }

  return (
    <SeccionLayout
      ayuda={AYUDA_SECCION["tiempos"]}
      ancho="formulario"
      titulo="Tiempos y escalación"
      descripcion="Días sin movimiento —sin notas ni cambios— para que un caso abierto cambie de color en la lista y dispare avisos. El semáforo de la lista y las alertas automáticas usan estos mismos valores."
    >
      <div className="card space-y-4">
        <BloqueSeccion titulo="Umbrales generales">
          <div className="grid gap-3 sm:grid-cols-3">
            <CampoDias
              etiqueta="Amarillo · por quemarse"
              hint="Se pinta amarillo y se le avisa a quien lo tiene asignado (si no hay, a quien lo abrió)."
              pill="bg-amber-500/10 text-amber-300 border-amber-500/30"
              value={valores.escalacion_dias_por_quemarse}
              onChange={(v) => set({ escalacion_dias_por_quemarse: v })}
            />
            <CampoDias
              etiqueta="Rojo · quemado"
              hint="Se pinta rojo y se le vuelve a avisar al asignado: requiere acción inmediata."
              pill="bg-red-500/10 text-red-300 border-red-500/30"
              value={valores.escalacion_dias_quemado}
              onChange={(v) => set({ escalacion_dias_quemado: v })}
            />
            <CampoDias
              etiqueta="Escalación a admin"
              hint="Además del asignado, se avisa a todos los administradores."
              pill="bg-red-500/10 text-red-300 border-red-500/30"
              value={valores.escalacion_dias_admin}
              onChange={(v) => set({ escalacion_dias_admin: v })}
            />
          </div>
        </BloqueSeccion>

        <BloqueSeccion
          titulo="Por estado del caso (opcional)"
          descripcion="Umbrales distintos según el estado. Deja un campo vacío para que ese umbral use el general; el texto gris del campo muestra el valor que se aplicaría."
        >
          <div className="overflow-x-auto">
            <div className="min-w-[300px] space-y-1">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-zinc-500">
                <span className="w-28 shrink-0 sm:w-48">Estado</span>
                <span className="w-14 text-center sm:w-16">Amarillo</span>
                <span className="w-14 text-center sm:w-16">Rojo</span>
                <span className="w-14 text-center sm:w-16">Admin</span>
              </div>
              {filas.map((f, i) => (
                <div key={f.estado} className="flex items-center gap-2">
                  <span className="w-28 shrink-0 text-xs text-zinc-300 sm:w-48">{f.estado}</span>
                  {CAMPOS_SLA.map((campo) => (
                    <input
                      key={campo}
                      type="number"
                      min={1}
                      max={365}
                      value={f[campo]}
                      placeholder={String(
                        campo === "pq"
                          ? (sla?.global_por_quemarse ?? "")
                          : campo === "q"
                            ? (sla?.global_quemado ?? "")
                            : (sla?.global_admin ?? ""),
                      )}
                      onChange={(e) =>
                        setFilas((rows) =>
                          rows.map((x, j) => (j === i ? { ...x, [campo]: e.target.value } : x)),
                        )
                      }
                      className="input w-14 px-1 text-center sm:w-16 sm:px-3"
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </BloqueSeccion>

        <PieGuardar
          sucio={hayCambios}
          guardando={guardando || guardandoSla}
          error={error}
          onGuardar={() => void guardarTodo()}
        />
      </div>
    </SeccionLayout>
  );
}

function CampoDias({
  etiqueta,
  hint,
  pill,
  value,
  onChange,
}: {
  etiqueta: string;
  /** Qué pasa al llegar a esos días; va a la vista, bajo el campo. */
  hint: string;
  pill: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block space-y-1">
      <span
        className={`inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${pill}`}
      >
        {etiqueta}
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={365}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="input w-24"
        />
        <span className="text-xs text-zinc-500">días</span>
      </div>
      <p className="text-[11px] leading-snug text-zinc-500">{hint}</p>
    </label>
  );
}
